import React, { useState, useEffect } from "react";
import Main from "../pages/Main";
import { exportTable } from "../utils/downloadExcel";
import { useLocation } from "react-router-dom";
import { fetchAddmoneyTrancation } from "../api/addmoneyApi";
import { autoDateFormat } from "../utils/autoDateFormate";
import { formatAmount } from "../utils/formatAmount";

import WalletHistoryBar from "../components/WalletHistoryBar";
import { useTxnDocActions } from "../hooks/useTxnDocActions";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  RowActions,
  StatusBadge,
  useListingState,
  TXN_STATUS_OPTIONS,
} from "../components/listing";
import { useNotify } from "../context/NotifyContext";

const AddmoneyTable = () => {
  const { notify } = useNotify();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const breadcrumbName = params.get("label") || "Add Money Report";
  const listing = useListingState({ sortBy: "date", sortDir: "desc" });
  const { viewTxn, printTxn, pdfTxn } = useTxnDocActions("addmoney");
  const [totalPages, setTotalPages] = useState(1);
  const [transactions, setAddmoneyTransaction] = useState([]);
  const [totalTransactions, setTotalbyAll] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const fetch_transaction = async () => {
    try {
      const result = await fetchAddmoneyTrancation(listing.query);
      if (result?.success) {
        const response = result?.data;
        const rows = response?.transactions || [];
        setAddmoneyTransaction(rows);
        setTotalPages(response?.pagination?.totalPages || 1);
        setTotalbyAll(response?.pagination?.total || 0);
        const apiTotal = Number(response?.totalAmount);
        setTotalAmount(
          Number.isFinite(apiTotal)
            ? apiTotal
            : rows.reduce((sum, r) => sum + (Number(r?.amount) || 0), 0)
        );
      } else {
        notify(result?.message || "Server Error", "error");
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetch_transaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
  ]);

  return (
    <Main showRightPanel={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "MATM Module" },
            { label: breadcrumbName },
          ]}
        />

        <WalletHistoryBar title={breadcrumbName} to="/add-money-ledger" />

        <div className="ds-card text-sm mt-2">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={TXN_STATUS_OPTIONS}
            showApply
            onApply={fetch_transaction}
            applyLabel="Load Records"
            onExport={() =>
              exportTable(
                "add-money-report",
                [
                  { label: "UNIQUE ID", key: "txnId" },
                  { label: "INITIATED DATE", value: (r) => (r?.date ? autoDateFormat(r.date) : "") },
                  { label: "AMOUNT", key: "amount" },
                  { label: "CHARGE", value: (r) => r?.charges?.totalDeduction ?? "" },
                  { label: "SETTLED", key: "finalAmount" },
                  { label: "PAYMENT MODE", key: "serviceType" },
                  { label: "ORDER ID", value: (r) => r?.paymentGatway?.orderId ?? "" },
                  { label: "EMAIL", value: (r) => r?.customer?.email ?? "" },
                  { label: "RP ORDER ID", value: (r) => r?.paymentGatway?.paymentId ?? "" },
                  { label: "BANK REF NUM", value: (r) => r?.paymentGatway?.rrn ?? "" },
                  {
                    label: "PAYMENT DATE",
                    value: (r) =>
                      r?.paymentGatway?.date
                        ? autoDateFormat(r.paymentGatway.date)
                        : r?.createdAt
                          ? autoDateFormat(r.createdAt)
                          : "",
                  },
                  { label: "PLATFORM", value: (r) => r?.meta?.platform ?? "" },
                  { label: "STATUS", value: (r) => r?.transactionStatus || r?.status || "" },
                  { label: "IS SETTLED", value: (r) => (r?.settlement?.isSettled ? "YES" : "NO") },
                ],
                transactions,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[900px]">
              <thead>
                <tr>
                  <th>UNIQUE ID</th>
                  <SortableTh
                    label="INITIATED DATE"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <SortableTh
                    label="AMOUNT"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>CHARGE</th>
                  <th>SETTLED</th>
                  <th>PAYMENT MODE</th>
                  <th>ORDER ID</th>
                  <th>EMAIL ADDRESS</th>
                  <th>RP ORDER ID</th>
                  <th>BANK REF NUM</th>
                  <th>PAYMENT DATE</th>
                  <th>CARD NUMBER</th>
                  <th>PLATFORM</th>
                  <th>SETTLEMENT TYPE</th>
                  <SortableTh
                    label="T.STATUS"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>IS SETTLED</th>
                  <th>PAYMENT SLIP</th>
                  <th className="ds-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.length === 0 ? (
                  <tr>
                    <td colSpan="18" className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  transactions?.map((row, index) => (
                    <tr key={row?._id || index}>
                      <td>
                        <CopyText value={row?.txnId} label="Txn ID" />
                      </td>
                      <td>{row?.date ? autoDateFormat(row?.date) : "--"}</td>
                      <td className="ds-td-amount">{formatAmount(row?.amount) ?? "--"}</td>
                      <td>{row?.charges?.totalDeduction ?? "--"}</td>
                      <td>{formatAmount(row?.finalAmount) ?? "--"}</td>
                      <td>{row?.serviceType ?? "--"}</td>
                      <td>
                        <CopyText value={row?.paymentGatway?.orderId} label="Order ID" />
                      </td>
                      <td>{row?.customer?.email ?? "--"}</td>
                      <td>{row?.paymentGatway?.paymentId ?? "--"}</td>
                      <td>
                        <CopyText value={row?.paymentGatway?.rrn} label="Bank Ref" />
                      </td>
                      <td>
                        {row?.paymentGatway?.date
                          ? autoDateFormat(row?.paymentGatway?.date)
                          : row?.createdAt
                            ? autoDateFormat(row?.createdAt)
                            : "--"}
                      </td>
                      <td>{row?.meta?.cardNumber ?? "XXXX-XXXX"}</td>
                      <td>{row?.meta?.platform ?? "--"}</td>
                      <td>{row?.settlement?.remarks ?? "--"}</td>
                      <td>
                        <StatusBadge
                          status={row?.transactionStatus || row?.status}
                        />
                      </td>
                      <td>{row?.settlement?.isSettled ? "YES" : "NO"}</td>
                      <td>
                        {row?.meta?.slipUrl ? (
                          <a
                            href={row.meta.slipUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="make-text-primary underline"
                          >
                            View
                          </a>
                        ) : (
                          "--"
                        )}
                      </td>
                      <td>
                        <RowActions
                          onView={() => viewTxn(row)}
                          onPdf={() => pdfTxn(row)}
                          onPrint={() => printTxn(row)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="ds-table-total">
                  <td colSpan={2} className="ds-table-total__label">
                    TOTAL AMOUNT
                  </td>
                  <td className="ds-table-total__value">₹ {formatAmount(totalAmount)}</td>
                  <td colSpan={15}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalTransactions}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
          />
        </div>
      </div>
    </Main>
  );
};

export default AddmoneyTable;
